import { checkPermissions } from "@elit-hair/elit-tools";
import { Tabs, TabsContent, TabsList, TabsTrigger, useIsMobile } from "@elit-hair/elit-ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LastCallCard } from "@/components/last-call-card";
import { PERMISSIONS } from "@/constants/permissions";
import { DialerContent } from "@/pages/home/components/dial-panel/dialer-content";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/useDashboardRole";
import { useCall } from "@/providers/hooks/use-call";

import { HeaderKpi } from "./kpi";
import { HeaderCallDialerContentProps, HeaderCallDialerTab, HeaderCallDialerVariant } from "./types/call-dialer.types";

function isHeaderCallDialerTab(value: string): value is HeaderCallDialerTab {
    return value === HeaderCallDialerTab.LAST_CALL || value === HeaderCallDialerTab.DIALER;
}

export function HeaderCallDialerContent({ variant = HeaderCallDialerVariant.TABS }: HeaderCallDialerContentProps) {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { permissionCodes } = useDashboardRole();
    const canUseDialer = checkPermissions(permissionCodes, [PERMISSIONS.CALL_DIALER]);
    const { calledPhoneNumber, setCalledPhoneNumber, handleHangupCall, callStatus, isCallActive, handleSendDigits } =
        useCall();

    const [activeTab, setActiveTab] = useState<HeaderCallDialerTab>(HeaderCallDialerTab.LAST_CALL);

    useEffect(() => {
        if (!canUseDialer && activeTab === HeaderCallDialerTab.DIALER) {
            setActiveTab(HeaderCallDialerTab.LAST_CALL);
        }
    }, [activeTab, canUseDialer]);

    const handleNumberClick = (num: string) => {
        if (isCallActive()) {
            handleSendDigits(num);
            return;
        }

        switch (num) {
            case "<":
                setCalledPhoneNumber(calledPhoneNumber.slice(0, -1));
                break;
            case "clear":
                setCalledPhoneNumber("");
                break;
            case "+":
                if (calledPhoneNumber.includes("+")) {
                    break;
                }

                setCalledPhoneNumber("+" + calledPhoneNumber);
                break;
            default:
                setCalledPhoneNumber(calledPhoneNumber + num);
                break;
        }
    };

    const lastCallContent = <LastCallCard />;
    const dialerContent = canUseDialer ? (
        <DialerContent
            calledPhoneNumber={calledPhoneNumber}
            setCalledPhoneNumber={setCalledPhoneNumber}
            handleHangup={handleHangupCall}
            handleNumberClick={handleNumberClick}
            callStatus={callStatus}
        />
    ) : null;

    if (variant === HeaderCallDialerVariant.LAST_CALL) {
        return lastCallContent;
    }

    if (variant === HeaderCallDialerVariant.DIALER) {
        return dialerContent;
    }

    return (
        <div className="flex h-full flex-col gap-3">
            {isMobile && (
                <div className="flex justify-center px-1" data-tour="kpi-area">
                    <HeaderKpi />
                </div>
            )}

            <Tabs
                className="min-h-0 flex-1"
                value={activeTab}
                onValueChange={(value) => {
                    if (isHeaderCallDialerTab(value)) {
                        setActiveTab(value);
                    }
                }}
            >
                <TabsList className="w-full">
                    <TabsTrigger value={HeaderCallDialerTab.LAST_CALL} className="w-full">
                        <span className="group-data-[collapsible=icon]:hidden">{t("last_call")}</span>
                    </TabsTrigger>

                    {canUseDialer ? (
                        <TabsTrigger value={HeaderCallDialerTab.DIALER} className="w-full">
                            <span className="group-data-[collapsible=icon]:hidden">{t("dialer")}</span>
                        </TabsTrigger>
                    ) : null}
                </TabsList>

                <TabsContent value={HeaderCallDialerTab.LAST_CALL} className="h-full w-full flex-1">
                    {lastCallContent}
                </TabsContent>

                {canUseDialer ? (
                    <TabsContent value={HeaderCallDialerTab.DIALER} className="h-full w-full flex-1">
                        {dialerContent}
                    </TabsContent>
                ) : null}
            </Tabs>
        </div>
    );
}
